import React, { FunctionComponent, useState, useEffect, MutableRefObject } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl, defineMessages, IntlShape } from 'react-intl';
import { useMedia } from 'react-media-match';
import { useHistory } from 'react-router';
import { browserSupportsGrid } from '@empiriecom/module-components/Grid';
import { Esi } from '@empiriecom/module-components/Esi';
import { Modal } from '@empiriecom/module-components/Modal';
import { Button } from '@empiriecom/module-components/Button';
import ProductRecoSlider from '@empiriecom/module-components/ProductRecoSlider';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import useTracking, { GtmGenericEvent } from '@empiriecom/module-components/hooks/useTracking';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { FragmentConfiguration } from '@/config';
import { GtmEventAddToCart } from '@/src/components/Tracking/types';
import { Product, SelectionRange } from '@/src/interfaces';
import addToBasketProvider from './AddToBasketProvider';
import { useProduct, useBasket, useCategory, useMadeToMeasure } from '../ProductContext';
import useBRToolParameter from '../hooks/useBRToolParameter';
import useUrlParameters from '../hooks/useUrlParameters';
import setVariation from '../PageContainer/setVariation';
import usePage from '../ProductContext/hooks/usePage';
import { InputError } from '../ProductSelection/Input/types';

const messages = defineMessages({
  LENGTH: {
    defaultMessage: 'lang',
    id: 'made-to-measure.dimension.LENGTH',
  },
  WIDTH: {
    defaultMessage: 'weit',
    id: 'made-to-measure.dimension.WIDTH',
  },
  addToBasketHeadline: {
    defaultMessage: 'In den Warenkorb gelegt',
    id: 'addToBasket.layer.headline',
  },
  criteoFallback: {
    defaultMessage: 'Gesponserte Artikel',
    id: 'addToBasket.layer.rde.criteo.headline-fallback',
  },
  notInStep: {
    defaultMessage: 'Es sind nur {step}{unit} Schritte erlaubt',
    id: 'made-to-measure.value.not-in-step',
  },
  prudsysATBProductsFallback: {
    defaultMessage: 'Andere Kunden kauften auch',
    id: 'addToBasket.layer.rde.prudsys.headline-fallback',
  },
  tooHigh: {
    defaultMessage: 'Der Artikel darf maximal {max}{unit} {dimension} sein',
    id: 'made-to-measure.value.too-high',
  },
  tooLow: {
    defaultMessage: 'Der Artikel muss mindestens {min}{unit} {dimension} sein',
    id: 'made-to-measure.value.too-low',
  },
});

export const AddToBasketButton = styled(Button)`
  padding: 0.4rem 1rem;
  margin: 0 auto;
  text-align: center;
  justify-content: center;
  align-items: center;
  height: 100%;
  border: 1px solid transparent;
  &&:hover,
  &&:focus {
    box-shadow: none;
  }
  &&:disabled svg {
    fill: transparent;
    width: 0;
    margin: 0;
  }
`;

const StyledBasket = styled(getIcon('Basket'))`
  margin-left: 0.5rem;
`;

export const trackEvent = (
  product: Product,
  quantity: number,
  dispatchGTMEvent: <T extends GtmGenericEvent>(event: T) => void,
) => {
  const {
    attributes: {
      description: { values },
    },
    dimensions,
  } = product;

  const longDescription = values
    .map((value) => (value.type === 'longDescription' ? value.values : ''))
    .join('');
  const dimVarSize = dimensions.filter((dimension) => dimension.name === 'Var_Size');
  const selectedVarSize = dimVarSize
    .map((dimension) =>
      dimension.values
        .filter((dimObj) => dimObj.isSelected === true && dimObj)
        .map((dimVal) => dimVal.displayText)
        .join(''),
    )
    .join('');
  const dimVarArticle = dimensions.filter((dimension) => dimension.name === 'Var_Article');
  const selectedVarArticle = dimVarArticle
    .map((dimension) =>
      dimension.values
        .filter((dimObj) => dimObj.isSelected === true && dimObj)
        .map((dimVal) => dimVal.displayText)
        .join(''),
    )
    .join('');
  const selectedDimensionValues = dimensions.map((dimension) =>
    dimension.values
      .filter((dimObj) => dimObj.isSelected === true)
      .map((dimVal) => dimVal.displayText),
  );
  dispatchGTMEvent<GtmEventAddToCart>({
    Merkzettel: 'nein',
    ecommerce: {
      add: {
        products: [
          {
            brand: product.brand?.name,
            category: product.defaultCategory,
            description: longDescription !== '' ? longDescription : undefined,
            dimension12: product.price.value > 0 ? ('nein' as 'nein') : ('ja' as 'ja'),
            dimension15: selectedVarSize !== 'false' ? selectedVarSize : undefined,
            dimension16: selectedVarArticle !== 'false' ? selectedVarArticle : undefined,
            dimension18: product.price.saving?.value > 0 ? ('ja' as 'ja') : ('nein' as 'nein'),
            dimension19: product.promotion ? product.promotion : undefined,
            dimension20: product.availability?.message,
            dimension21: product.brand?.name,
            id: product.orderNumber,
            imageUrl: product.images[0],
            mkz: product.marketcode?.toFixed(),
            name: product.name,
            price: product.price.value.toFixed(),
            quantity,
            sku: product.sku,
            variant: selectedDimensionValues.join(', '),
          },
        ],
      },
    },
    event: 'EECaddToCart',
  });
};

const EsiContainer = styled.div`
  #modal-esi-wrapper .add-product-cont .basket-articles .basket-product-line .basket-price {
    width: auto;
  }
`;

export const validateInput = (
  axisType: string,
  intl: IntlShape,
  inputRef: MutableRefObject<HTMLInputElement>,
  range: SelectionRange,
  setError: (error: InputError) => void,
): boolean => {
  const { step, min, max, unit } = range;
  const dimension = messages[axisType] ? intl.formatMessage(messages[axisType]) : '';
  if (inputRef?.current?.value === '' || parseFloat(inputRef?.current?.value) < min) {
    setError({
      message: intl.formatMessage(messages.tooLow, { dimension, min, unit }),
      type: 'TOO_LOW',
    });
    return true;
  }
  if (parseFloat(inputRef?.current?.value) > max) {
    setError({
      message: intl.formatMessage(messages.tooHigh, { dimension, max, unit }),
      type: 'TOO_HIGH',
    });
    return true;
  }
  if (parseFloat(inputRef?.current?.value) % step !== 0) {
    setError({
      message: intl.formatMessage(messages.notInStep, { step, unit }),
      type: 'NOT_IN_STEP',
    });
    return true;
  }
  setError(null);
  return false;
};

export const AddToBasketModal: FunctionComponent = () => {
  const dispatchGTMEvent = useTracking();
  const { product, printProductId } = useProduct();
  const history = useHistory();
  const {
    api: {
      values: { apiAddToBasketESI },
    },
    companyId,
    recommendationAPI,
  } = useConfig<FragmentConfiguration>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [configString, setConfigString] = useState<string>(useBRToolParameter());
  const { availability, brand, orderNumber, price, unit, sku } = product;

  const intl = useIntl();
  const isLegacyBrowser = !browserSupportsGrid();
  const {
    quantity,
    services,
    addToBasketRequested,
    setAddToBasketRequested,
    requestedSKU,
    setRequestedSKU,
  } = useBasket();
  const { width, widthInputRef, length, lengthInputRef, engraving } = useMadeToMeasure();
  const { category: dataCategory, query } = useCategory();
  const { isBot } = usePage();
  const category = query ? 'Suche' : dataCategory;

  useEffect(() => {
    let confString: string = configString;
    if (
      width !== null ||
      (widthInputRef?.current?.value !== '' && typeof widthInputRef?.current?.value !== 'undefined')
    ) {
      confString = confString.concat(
        confString.length > 0
          ? `&WidthInCentimeters=${widthInputRef?.current?.value}`
          : `WidthInCentimeters=${widthInputRef?.current?.value}`,
      );
    }
    if (
      length !== null ||
      (lengthInputRef?.current?.value !== '' &&
        typeof lengthInputRef?.current?.value !== 'undefined')
    ) {
      confString = confString.concat(
        confString.length > 0
          ? `&LengthInCentimeters=${lengthInputRef?.current?.value}`
          : `LengthInCentimeters=${lengthInputRef?.current?.value}`,
      );
    }
    if (typeof engraving !== 'undefined' && engraving !== null && engraving !== '') {
      confString = confString.concat(
        confString.length > 0 ? `&Engraving=${engraving}` : `Engraving=${engraving}`,
      );
    }
    if (confString !== '') {
      setConfigString(confString);
    }
  }, [width, length, engraving, addToBasketRequested]);

  let skuToCheck = requestedSKU || sku;
  // transfer new sku to legacy format for add to basket call
  if (skuToCheck?.split('-').length <= 2) {
    skuToCheck = `${skuToCheck}-${sku?.split('-')[2]}`;
  }
  useEffect(() => {
    if (addToBasketRequested && skuToCheck?.split('-')[0] === requestedSKU?.split('-')[0]) {
      setIsModalOpen(true);
    }
  }, [addToBasketRequested]);

  const atbUrl = useUrlParameters(
    apiAddToBasketESI,
    {
      configString: encodeURIComponent(configString),
      quantity: quantity.toFixed(),
      quantityUnit: typeof unit !== 'undefined' && unit !== '' ? unit : 'Stk',
      services: encodeURI(services.length > 0 ? services.join(';') : ''),
      shopname: encodeURI(category),
      shoppath: encodeURI(category),
      sku: printProductId ? `${skuToCheck}$${printProductId}` : skuToCheck,
    },
    true,
  );
  const modalProps = {
    large: useMedia({ desktop: true, mobile: false, tablet: true }),
  };

  useEffect(() => {
    if (isModalOpen && availability?.state !== 'UNSELECTED') {
      // send EECAddToCart
      trackEvent(product, quantity, dispatchGTMEvent);
    }
  }, [isModalOpen, availability?.state]);

  /* Dirty hack to show basket value in IS7 ESI. This is a Provisorium! */
  setTimeout(() => {
    if (isModalOpen && typeof window !== 'undefined' && (window as any).jQuery) {
      (window as any).jQuery(document).trigger('ecBasketUpdated');
    }
  }, 1500);

  setTimeout(() => {
    if (
      isModalOpen &&
      typeof document !== 'undefined' &&
      document.getElementById('atb-modal-esi') !== null
    ) {
      if (
        typeof document
          .getElementById('atb-modal-esi')
          .getElementsByClassName('modalbox-closebutton-link')[0] !== 'undefined'
      ) {
        document
          .getElementById('atb-modal-esi')
          .getElementsByClassName('modalbox-closebutton-link')[0]
          .addEventListener('click', () => {
            if (!isLegacyBrowser) {
              addToBasketProvider.toggleIcon();
            }
            setIsModalOpen(false);
            setAddToBasketRequested(false);
            setVariation(
              `${requestedSKU?.split('-')[0]}-${requestedSKU?.split('-')[1]}`,
              history,
              isBot,
            );
            setRequestedSKU('');
          });
      }
    }
  }, 4000);

  return (
    <Modal
      testId="basket-modal"
      onRequestClose={() => {
        if (!isLegacyBrowser) {
          addToBasketProvider.toggleIcon();
        }
        setIsModalOpen(false);
        setAddToBasketRequested(false);
        setVariation(
          `${requestedSKU?.split('-')[0]}-${requestedSKU?.split('-')[1]}`,
          history,
          isBot,
        );
        setRequestedSKU('');
      }}
      open={isModalOpen}
      headline={intl.formatMessage(messages.addToBasketHeadline)}
      // modal props need to be passed this way
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...modalProps}
    >
      <EsiContainer id="atb-modal-esi">
        <div id="modal-esi-wrapper">
          <Esi src={atbUrl} />
        </div>
        {companyId !== 'emma' && (
          <>
            <ProductRecoSlider
              recoApi={recommendationAPI}
              pageTemplate="atb"
              headline={intl.formatMessage(messages.prudsysATBProductsFallback)}
              rdeRequest={{
                articleNo: orderNumber,
                available: availability && availability.state === 'NOT_AVAILABLE' ? 0 : 1,
                brand: brand ? brand.name : '',
                cid: dataCategory,
                price: parseFloat(price.value.toFixed(2)),
                rdeType: 'prudSysAddToBasket',
              }}
            />
            <ProductRecoSlider
              recoApi={recommendationAPI}
              pageTemplate="atb"
              headline={intl.formatMessage(messages.criteoFallback)}
              rdeRequest={{
                articleNo: orderNumber,
                available: availability && availability.state === 'NOT_AVAILABLE' ? 0 : 1,
                brand: brand ? brand.name : '',
                cid: dataCategory,
                price: parseFloat(price.value.toFixed(2)),
                rdeType: 'criteoProducts',
              }}
            />
          </>
        )}
      </EsiContainer>
    </Modal>
  );
};

type AddToBasketProps = {
  /** function to force dimension selection */
  setHighlightUnselectedDimension: (setHighlight: boolean) => void;
  isSelected: boolean;
};

/**
 * This is the `AddToBasket` button component to handle the adding products to the basket.
 * This component is sensible to the selection and availabiliy of the displayed product.
 * A modal layer with the added product or a selection dialog for an unselected dimension is displayed on click.
 */
export const AddToBasket: FunctionComponent<AddToBasketProps> = ({
  setHighlightUnselectedDimension,
  isSelected,
}) => {
  const {
    addToBasketButton: { layout },
  } = useConfig<FragmentConfiguration>();
  const intl = useIntl();
  const { product } = useProduct();
  const { setAddToBasketRequested, setRequestedSKU } = useBasket();

  const {
    engravingError,
    lengthInputRef,
    setShowEngravingError,
    setLengthError,
    setWidthError,
    widthInputRef,
  } = useMadeToMeasure();

  // return null if product is not (yet) defined.
  if (!product) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { availability, sku, dimensions } = product;
  const isAvailable = availability?.state !== 'UNSELECTED' ? availability?.isOrderable : true;

  const widthAxis = dimensions.filter((dimension) => dimension.axisType === 'WIDTH')[0];
  const lengthAxis = dimensions.filter((dimension) => dimension.axisType === 'LENGTH')[0];

  return (
    <>
      <AddToBasketButton
        data-testid="add-to-basket"
        disabled={!isAvailable}
        fullWidth
        layout={layout}
        onClick={() => {
          let widthErrorFlag = false;
          let lengthErrorFlag = false;
          if (engravingError !== null) {
            setShowEngravingError(true);
          }
          if (typeof widthAxis !== 'undefined') {
            widthErrorFlag = validateInput(
              'WIDTH',
              intl,
              widthInputRef,
              widthAxis.values[0]?.range,
              setWidthError,
            );
          }
          if (typeof lengthAxis !== 'undefined') {
            lengthErrorFlag = validateInput(
              'LENGTH',
              intl,
              lengthInputRef,
              lengthAxis.values[0]?.range,
              setLengthError,
            );
          }
          if (!lengthErrorFlag && !widthErrorFlag && engravingError === null) {
            setShowEngravingError(false);
            if (!isSelected) {
              setHighlightUnselectedDimension(true);
            } else if (isAvailable) {
              setAddToBasketRequested(true);
              setRequestedSKU(sku);
            }
          }
        }}
        size="large"
        uppercase={false}
      >
        {isAvailable ? (
          <>
            <FormattedMessage id="add-to-basket.label" defaultMessage="In den Warenkorb legen" />
            <StyledBasket size="2rem" />
          </>
        ) : (
          <>
            <FormattedMessage id="add-to-basket.sold-out" defaultMessage="Ausverkauft" />
            <StyledBasket size="2rem" />
          </>
        )}
      </AddToBasketButton>
    </>
  );
};

export default AddToBasket;
